import { useEffect, useId, useRef, type ComponentRef } from "react";
import { useController, useFormContext, type FieldPath, type FieldValues } from "react-hook-form";

import { Label } from "../../atoms/label";
import { cn, platformProps } from "../../lib";
import { webOnly } from "../../lib/web-role";
import { Box, type Pressable } from "../../primitives";
import { RadioGroup, RadioGroupItem } from "../radio-group";
import type {
  RadioGroupIndicator,
  RadioGroupOrientation,
  RadioGroupSize,
} from "../radio-group/types";
import { FormFieldMessage } from "./form-field-message";
import type { FormFieldBaseProps } from "./types";

export interface RadioGroupOption {
  /** Unique value for this option */
  value: string;
  /** Display label shown beside the radio item */
  label: string;
  /** Whether this option is disabled */
  disabled?: boolean;
}

export type FormRadioGroupProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldBaseProps<TFieldValues, TName> & {
  /** Array of options to render as radio items */
  options: RadioGroupOption[];
  /** Layout direction of the radio items */
  orientation?: RadioGroupOrientation;
  /** Size of each radio item */
  size?: RadioGroupSize;
  /** Indicator style: 'fill' = filled circle (default), 'check' = check icon */
  indicator?: RadioGroupIndicator;
  /** Disables all radio items in the group */
  disabled?: boolean;
  /** Names the group for assistive technology; `label` when omitted */
  accessibilityLabel?: string;
  /**
   * Prefix for the items' ids (`${id}-${value}`) and the group's test
   * identifier; a generated one when omitted. The group itself has no DOM id.
   */
  id?: string;
};

export const FormRadioGroup = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  required = false,
  helperText,
  hideErrorMessage = false,
  errorMessage,
  containerClassName,
  testID,
  options,
  orientation = "vertical",
  size = "md",
  indicator = "fill",
  disabled = false,
  accessibilityLabel,
  id: idProp,
}: FormRadioGroupProps<TFieldValues, TName>) => {
  // A caller's id wins, as the other labelled wrappers keep theirs (Form #19)
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const { control } = useFormContext<TFieldValues>();
  const { field, fieldState } = useController<TFieldValues, TName>({ name, control });
  const displayError =
    fieldState.error && !hideErrorMessage ? (errorMessage ?? fieldState.error.message) : undefined;

  // On web, RHF focuses the group on a failed submit (Form #2) through one
  // proxy: asked to focus, it picks the checked enabled row, else the first
  // enabled one, as they are then. Native registers nothing: a row is a View,
  // whose focus() sends a TextInput command. With no enabled row the proxy
  // focuses nothing, and RHF stops at this group.
  const rows = useRef(new Map<string, ComponentRef<typeof Pressable>>());
  const latest = useRef({ value: field.value as unknown, options, disabled });
  useEffect(() => {
    latest.current = { value: field.value, options, disabled };
  });
  const registerFocus = webOnly(field.ref);
  useEffect(() => {
    registerFocus?.({
      focus: () => {
        const { value, options: current, disabled: groupDisabled } = latest.current;
        if (groupDisabled) return;
        const enabled = current.filter((o) => !o.disabled && rows.current.has(o.value));
        const target = enabled.find((o) => o.value === value) ?? enabled[0];
        if (target) rows.current.get(target.value)?.focus();
      },
    });
  }, [registerFocus]);
  const rowRef = (value: string) => (node: ComponentRef<typeof Pressable> | null) => {
    if (node) rows.current.set(value, node);
    else rows.current.delete(value);
  };

  return (
    <Box
      {...platformProps({ slot: "form-radio-group", testID })}
      className={cn("gap-spacing-xxs flex flex-col", containerClassName)}
    >
      {label && (
        // No htmlFor: a group can't be a label's target; RadioGroup is named below
        <Label required={required} disabled={disabled}>
          {label}
        </Label>
      )}
      <RadioGroup
        value={field.value ?? ""}
        onValueChange={field.onChange}
        // Leaving the group marks the field touched (RadioGroup #1; web)
        onBlur={field.onBlur}
        orientation={orientation}
        disabled={disabled}
        size={size}
        indicator={indicator}
        testID={id}
        accessibilityLabel={accessibilityLabel ?? label}
      >
        {options.map((option) => (
          <RadioGroupItem
            key={option.value}
            ref={rowRef(option.value)}
            value={option.value}
            id={`${id}-${option.value}`}
            label={option.label}
            disabled={option.disabled}
          />
        ))}
      </RadioGroup>
      <FormFieldMessage error={displayError} helperText={helperText} />
    </Box>
  );
};

FormRadioGroup.displayName = "FormRadioGroup";
